import {
  type StyleConfigProps,
  type TransformsElementProps,
  type VisibilityElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementLabelStyleProps,
  type ElementLink,
  type ImageBehavior,
  type ImageRatio,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { type SelectOptionWithImage, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

export const DEFAULT_IMAGE_URI = 'assets/images/image-placeholder.jpg';

export const PLACEHOLDERS_PATH = 'assets/image-placeholders';

export type ImageEmptyThumbnail =
  | 'avatar-1'
  | 'avatar-2'
  | 'custom-text'
  | 'image-1'
  | 'image-2'
  | 'initials';

export const EMPTY_ICON_MAP: Record<ImageEmptyThumbnail, string> = {
  'avatar-1': 'ies-avatar-1',
  'avatar-2': 'ies-avatar-2',
  'custom-text': 'ies-custom-text',
  'image-1': 'ies-image-1',
  'image-2': 'ies-image-2',
  initials: 'ies-initials',
};
export const IMAGE_EMPTY_STATE_OPTIONS: SelectOptionWithImage<ImageEmptyThumbnail, string>[] = [
  { label: 'Avatar #1', value: 'avatar-1', image: EMPTY_ICON_MAP['avatar-1'] },
  { label: 'Avatar #2', value: 'avatar-2', image: EMPTY_ICON_MAP['avatar-2'] },
  { label: 'Image #1', value: 'image-1', image: EMPTY_ICON_MAP['image-1'] },
  { label: 'Image #2', value: 'image-2', image: EMPTY_ICON_MAP['image-2'] },
  { label: 'Initials', value: 'initials', image: EMPTY_ICON_MAP['initials'] },
  { label: 'Custom Text', value: 'custom-text', image: EMPTY_ICON_MAP['custom-text'] },
];

export type ImageClickAction = 'download' | 'expand';
export type ImagePropsModelBase = CanvasElementPropertiesWithData &
  StyleConfigProps &
  VisibilityElementProps &
  TransformsElementProps;

export type ImagePropsModel = ImagePropsModelBase & {
  content: {
    imageSource: {
      enabled: boolean;
      url: string;
      placeholder?: {
        fileName: string;
        width?: string;
        height?: string;
      };
    };
    options: {
      allowEdit: boolean;
      altText: string;
      behavior: ImageBehavior;
      clickAction?: ImageClickAction;
      emptyText: string;
      emptyThumbnail: ImageEmptyThumbnail;
      forceMaxHeight?: string;
      forceMaxWidth?: string;
      forceRatio?: ImageRatio;
      forceRatioCustom?: string;
      initialsColumn?: string;
      readonly: boolean;
      unlimitedUpload: boolean;
      uploadMax: number;
    };
    title: ElementLabelPropsWithPositionAndWidth;
    link?: ElementLink;
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    image: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      visualEffect?: PropertiesConfigInputVisualEffectModel;
    };
    general: {
      title: PropertiesConfigInputColorModel;
    };
    labelBlock: ElementLabelStyleProps;
    outline: {
      color: PropertiesConfigInputColorModel;
      width: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
    };
  };
};
